import { produce } from 'immer';
import React, { ReactNode, useContext, useEffect, useMemo } from 'react';
import {
  BehaviorSubject,
  bufferTime,
  debounceTime,
  filter,
  interval,
  skip,
  Subject,
} from 'rxjs';
import { useCurrentUserId } from '../../../../functions/auth/utils';
import useBehaviorSubject from '../../../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../../../functions/hooks/useCreation';
import { makeDebug } from '../../../../utils/makeDebug';
import { ChatSettings, useChatSettings } from '../functions/useChatSettings';
import { ChatEvent } from '../models/chat-event';
import { ClientChatMessage, MsgSendStatus } from '../models/chatMessage';
import { ChatUser } from '../models/chatUser';
import { ChatStorageManager } from '../storage/ChatStorageManager';
import { DefaultChatStorageManager } from '../storage/impl/DefaultChatStorageManager';
import { isChatDebugEnabled } from '../utils';
import { ChatGatewayContextProvider } from './ChatGatewayContext';
import { ChatSessionContextProvider } from './ChatSessionContext';

const kDefaultMessageExpireTime = 120 * 1000; // 2 mins
const kMessageExpireCheckInterval = 10 * 1000; // on every 30 seconds

const debug = makeDebug('chat:global', isChatDebugEnabled('global'));

export type ActiveChat = {
  roomId: string;
  targetUserId: string;
};

export type ReceivedChatNotificationSummary = {
  senders: Map<string, ChatUser>;
  messageCount: number;
};

export interface GlobalChatContextProps {
  activeChat$: BehaviorSubject<ActiveChat | undefined>;
  pendingMessages$: BehaviorSubject<ClientChatMessage[]>;
  receivedChatNotificationSummary$: BehaviorSubject<ReceivedChatNotificationSummary>;
  events$: Subject<ChatEvent>;
  storage: ChatStorageManager;
  settings: ChatSettings | undefined;
}

export const GlobalChatContext = React.createContext<GlobalChatContextProps>(
  null as any,
);

export const useGlobalChatContext = () => useContext(GlobalChatContext);

export const useChatStorage = () => useGlobalChatContext().storage;

export const useActiveChat = () => {
  const { activeChat$ } = useGlobalChatContext();
  const [activeChat] = useBehaviorSubject(activeChat$);
  return activeChat;
};

export const ChatGlobalContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const currentUserId = useCurrentUserId();

  const storage = useCreation(
    () => new DefaultChatStorageManager(currentUserId),
    [currentUserId],
  );

  const activeChat$ = useCreation(
    () => new BehaviorSubject<ActiveChat | undefined>(undefined),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const events$ = useCreation(() => new Subject<ChatEvent>(), [currentUserId]);

  const receivedChatNotificationSummary$ = useCreation(
    () =>
      new BehaviorSubject<ReceivedChatNotificationSummary>({
        senders: new Map(),
        messageCount: 0,
      }),
    [],
  );

  const { pendingMessages$ } = useChatPendingMessagesManager(
    events$,
    storage,
    currentUserId,
  );

  const { settings } = useChatSettings();

  const value = useMemo<GlobalChatContextProps>(() => {
    return {
      storage,
      activeChat$,
      events$,
      pendingMessages$,
      receivedChatNotificationSummary$,
      settings: settings ?? undefined,
    };
  }, [
    storage,
    activeChat$,
    events$,
    pendingMessages$,
    receivedChatNotificationSummary$,
    settings,
  ]);

  return (
    <GlobalChatContext.Provider value={value}>
      <ChatGatewayContextProvider>
        <ChatSessionContextProvider>{children}</ChatSessionContextProvider>
      </ChatGatewayContextProvider>
    </GlobalChatContext.Provider>
  );
};

/**
 * A hook that implements pending messages loading, saving and responds to chat
 * events automatically.
 * @param events$ chat events subject
 * @param storage chat storage manager
 * @param currentUserId current user id
 */
const useChatPendingMessagesManager = (
  events$: Subject<ChatEvent>,
  storage: ChatStorageManager,
  currentUserId: string | undefined,
) => {
  const pendingMessages$ = useCreation(
    () => new BehaviorSubject<ClientChatMessage[]>([]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId],
  );

  useEffect(() => {
    // load local cached pending messages
    void storage.getPendingMessages().then(messages => {
      pendingMessages$.next(messages);
    });

    // automatically save pending messages to local cache on change
    const sub = pendingMessages$
      .pipe(skip(1), debounceTime(1000))
      .subscribe(messages => {
        void storage.savePendingMessages(messages).then(() => {
          debug('pending messages saved to local cache');
        });
      });
    return () => sub.unsubscribe();
  }, [storage, pendingMessages$]);

  useEffect(() => {
    // respond to chat events automatically by adding/removing pending messages
    // or update the status of pending messages on failure.
    const sub = events$
      .pipe(
        bufferTime(1000),
        filter(x => x.length > 0),
      )
      .subscribe(events => {
        const pendingMessages = pendingMessages$.getValue();

        const updated = produce(pendingMessages, draft => {
          for (const event of events) {
            debug('handling chat event %s... ', event.type);
            switch (event.type) {
              case 'sending-message': {
                const index = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (index >= 0) {
                  draft[index] = event.message;
                } else {
                  draft.push(event.message);
                }
                break;
              }
              case 'send-message-success': {
                const index = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (index >= 0) {
                  draft.splice(index, 1);
                }
                break;
              }
              case 'send-message-failed': {
                const index = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (index >= 0) {
                  console.error(event.error);
                  draft[index].sendStatus = MsgSendStatus.fail;
                  draft[index].lastSendErrorMsg =
                    typeof event.error === 'string'
                      ? event.error
                      : (event.error as Error)?.message ?? 'Unknown error';
                }
                break;
              }

              case 'messages-loaded': {
                // remove pending messages.
                const messageClientIdSet = new Set(
                  event.messages.map(x => x.clientId),
                );
                const pendingMessageClientIds = pendingMessages
                  .map(x => x.clientId)
                  .filter(x => messageClientIdSet.has(x));
                for (const clientId of pendingMessageClientIds) {
                  const index = draft.findIndex(x => x.clientId === clientId);
                  if (index >= 0) {
                    debug(
                      'removing pending message at index %d: %O',
                      index,
                      draft[index],
                    );
                    draft.splice(index, 1);
                  }
                }
                break;
              }

              case 'remove-pending-message': {
                const index = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (index >= 0) {
                  debug('failed pending message is removed: %O', event.message);
                  draft.splice(index, 1);
                  events$.next({
                    type: 'pending-message-removed',
                    message: event.message,
                  });
                }
                break;
              }
            }
          }
        });
        if (pendingMessages !== updated) {
          debug('pending messages updated');
          pendingMessages$.next(updated);
        }
      });
    return () => sub.unsubscribe();
  }, [events$, pendingMessages$]);

  useEffect(() => {
    // expires pending messages in a timely fashion
    const sub = interval(kMessageExpireCheckInterval).subscribe(() => {
      const messages = pendingMessages$.getValue();
      const expiredMessageIdSet = new Set<string>();
      const updated = produce(messages, draft => {
        for (const message of draft) {
          if (
            message.sendStatus === MsgSendStatus.sending &&
            Date.now() - message.lastSendTime > kDefaultMessageExpireTime
          ) {
            message.sendStatus = MsgSendStatus.fail;
            message.lastSendErrorMsg = 'Timeout';
            expiredMessageIdSet.add(message.clientId);
            debug('pending message expired: %O', message);
          }
        }
      });
      if (messages !== updated) {
        pendingMessages$.next(updated);
        const expiredMessages = updated.filter(x =>
          expiredMessageIdSet.has(x.clientId),
        );
        events$.next({
          type: 'messages-expired',
          messages: expiredMessages,
        });
      }
    });
    return () => sub.unsubscribe();
  }, [events$, pendingMessages$]);

  return { pendingMessages$ };
};
